import { z } from 'zod';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

const requiredInteger = z.number().int();
const requiredDecimal = z.number();

/**
 * One Guide × Customer × Day row from the Members Progressed longitudinal spec.
 * Property names match the warehouse columns so a later request result can
 * replace the fixture without a rename layer.
 */
export const ManagerOperatingSystemLongitudinalMetricsRowSchema = z.object({
  row_id: requiredInteger,
  guide_id: z.string(),
  customer: z.string(),
  /** Effective business date, `YYYY-MM-DD`. */
  day: z.string().regex(ISO_DAY),
  is_reema_working_day: z.boolean(),
  loaded_at: z.string().datetime(),
  queued_at_month_start: requiredInteger,
  reengaged_count: requiredInteger,
  reengaged_cumulative_monthly: requiredInteger,
  reengaged_benchmark_monthly: requiredDecimal,
  reengaged_remaining_pace: requiredDecimal,
  reengaged_benchmark_adjusted: requiredDecimal,
  re_calls_attempts_count: requiredInteger,
  re_calls_attempts_count_cumulative_weekly: requiredInteger,
  re_calls_attempts_count_benchmark_weekly: requiredDecimal,
  re_calls_attempts_remaining_pace: requiredDecimal,
  re_calls_attempts_benchmark_adjusted: requiredDecimal,
  re_texts_attempts_count: requiredInteger,
  re_texts_attempts_count_cumulative_weekly: requiredInteger,
  re_texts_attempts_count_benchmark_weekly: requiredDecimal,
  re_texts_attempts_remaining_pace: requiredDecimal,
  re_texts_attempts_benchmark_adjusted: requiredDecimal,
  re_home_visits_attempts_count: requiredInteger,
  re_home_visits_attempts_count_cumulative_weekly: requiredInteger,
  re_home_visits_attempts_count_benchmark_weekly: requiredDecimal,
  re_home_visits_attempts_remaining_pace: requiredDecimal,
  re_home_visits_attempts_benchmark_adjusted: requiredDecimal,
  queued_outreach_completed: requiredInteger,
  queued_outreach_needed: requiredInteger,
  queued_outreach_percent_complete: requiredDecimal.nullable(),
  queued_outreach_benchmark: requiredDecimal,
  goal_progressed_members_count: requiredInteger,
  goal_progressed_members_cumulative_monthly: requiredInteger,
  goal_progressed_members_benchmark_monthly: requiredDecimal,
  goal_progressed_members_remaining_pace: requiredDecimal,
  goal_progressed_members_benchmark: requiredDecimal,
  members_progressed_count: requiredInteger,
  members_progressed_cumulative_monthly: requiredInteger,
  members_progressed_benchmark_monthly: requiredDecimal,
  members_progressed_remaining_pace: requiredDecimal,
  members_progressed_benchmark: requiredDecimal,
});

/**
 * Validated longitudinal metrics row.
 */
export type ManagerOperatingSystemLongitudinalMetricsRow = z.infer<
  typeof ManagerOperatingSystemLongitudinalMetricsRowSchema
>;

/**
 * Longitudinal rows for the selected MOS window and supervised scope.
 */
export interface ManagerOperatingSystemLongitudinalMetricsDto {
  rows: ManagerOperatingSystemLongitudinalMetricsRow[];
}

/** Stable outcome ids used in portfolio cards and workflow URLs. */
export const MANAGER_OPERATING_SYSTEM_OUTCOME_IDS = [
  'new-engagement',
  'members-progressed',
] as const;

/** Portfolio outcome identifier. */
export type ManagerOperatingSystemOutcomeId =
  (typeof MANAGER_OPERATING_SYSTEM_OUTCOME_IDS)[number];

/**
 * Whether `value` is a portfolio outcome id.
 * @param value - Raw route or payload segment.
 * @returns True for `new-engagement` or `members-progressed`.
 */
export function isManagerOperatingSystemOutcomeId(
  value: string
): value is ManagerOperatingSystemOutcomeId {
  return (
    MANAGER_OPERATING_SYSTEM_OUTCOME_IDS as readonly string[]
  ).includes(value);
}

/** Card status after scored workflow checks. */
export type ManagerOperatingSystemOutcomeStatus =
  | 'on-track'
  | 'needs-attention';

/** One workflow check, including checks the spec cannot score. */
export type ManagerOperatingSystemOutcomeCheckStatus =
  | 'on-track'
  | 'needs-attention'
  | 'not-scored';

/**
 * How a workflow check's actual and target are displayed.
 * `percent` values are 0–1 ratios.
 */
export type ManagerOperatingSystemOutcomeCheckUnit = 'count' | 'percent';

/**
 * One workflow check on an outcome card.
 */
export interface ManagerOperatingSystemOutcomeCheckItemDto {
  id: string;
  label: string;
  status: ManagerOperatingSystemOutcomeCheckStatus;
  unit: ManagerOperatingSystemOutcomeCheckUnit;
  /** Null when the check is not scored. */
  actual: number | null;
  /** Null when the check is not scored. */
  target: number | null;
}

/**
 * Scored and unscored workflow checks for one outcome.
 */
export interface ManagerOperatingSystemOutcomeChecksDto {
  onTrack: number;
  needAttention: number;
  notScored: number;
  items: ManagerOperatingSystemOutcomeCheckItemDto[];
}

/**
 * One working day in the selected window.
 */
export interface ManagerOperatingSystemOutcomeTrendPointDto {
  /** `YYYY-MM-DD`. */
  day: string;
  actual: number;
  target: number;
}

/**
 * Diagnose and Act copy for the first off-track check, or the on-track line.
 */
export interface ManagerOperatingSystemOutcomeDiagnosisDto {
  headline: string;
  detail: string;
  action: string;
  /** Actual on the first off-track check. Null when the workflow is on track. */
  actual: number | null;
  /** Target on the first off-track check. Null when the workflow is on track. */
  target: number | null;
  /**
   * Unit of the first off-track check. Null when the workflow is on track.
   */
  unit: ManagerOperatingSystemOutcomeCheckUnit | null;
}

/**
 * One outcome card for a guide.
 */
export interface ManagerOperatingSystemOutcomeCardDto {
  outcomeId: ManagerOperatingSystemOutcomeId;
  label: string;
  actual: number;
  target: number;
  /** `target - actual`. Negative when the guide is ahead of target. */
  gap: number;
  /**
   * `actual / target * 100`. Zero when `target` is zero so the ring never
   * divides by zero. May exceed 100 when actual is above target.
   */
  percentOfTarget: number;
  status: ManagerOperatingSystemOutcomeStatus;
  checks: ManagerOperatingSystemOutcomeChecksDto;
  trend: ManagerOperatingSystemOutcomeTrendPointDto[];
  diagnosis: ManagerOperatingSystemOutcomeDiagnosisDto;
}

/**
 * One supervised guide and the outcome cards in the selected window.
 */
export interface ManagerOperatingSystemOutcomePortfolioGuideDto {
  guideId: string;
  name: string;
  /** Customer names from in-window longitudinal rows, sorted. */
  customerNames: string[];
  outcomes: ManagerOperatingSystemOutcomeCardDto[];
}

/**
 * Team Details portfolio derived from new-engagement metrics and longitudinal rows.
 */
export interface ManagerOperatingSystemOutcomePortfolioDto {
  guides: ManagerOperatingSystemOutcomePortfolioGuideDto[];
}
